import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Upload, List, LogOut, FileText, CreditCard, Download, Landmark, Scale, History, ChevronLeft, ChevronRight, Receipt, Building2, PieChart } from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', testId: 'nav-dashboard' },
  { to: '/upload', icon: Upload, label: 'Upload', testId: 'nav-upload' },
  { to: '/upload-history', icon: History, label: 'Upload History', testId: 'nav-upload-history' },
  { to: '/transactions', icon: List, label: 'Transactions', testId: 'nav-transactions' },
  { to: '/accounts', icon: Landmark, label: 'Accounts', testId: 'nav-accounts' },
  { to: '/downloads', icon: Download, label: 'Downloads', testId: 'nav-downloads' },
  { to: '/reconciliation', icon: Scale, label: 'Reconciliation', testId: 'nav-reconciliation' },
  { type: 'divider', label: 'Business' },
  { to: '/business/dashboard', icon: PieChart, label: 'Dashboard', testId: 'nav-business-dashboard' },
  { to: '/business/upload', icon: Upload, label: 'Upload', testId: 'nav-business-upload' },
  { to: '/business/upload-history', icon: History, label: 'Upload History', testId: 'nav-business-upload-history' },
  { to: '/business/transactions', icon: Building2, label: 'Transactions', testId: 'nav-business-transactions' },
  { to: '/business/accounts', icon: Landmark, label: 'Accounts', testId: 'nav-business-accounts' },
  { to: '/business/downloads', icon: Download, label: 'Downloads', testId: 'nav-business-downloads' },
  { to: '/business/reconciliation', icon: Scale, label: 'Reconciliation', testId: 'nav-business-reconciliation' },
  { type: 'divider', label: 'Other' },
  { to: '/salary-slips', icon: Receipt, label: 'Salary Slips', testId: 'nav-salary-slips' },
];

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <aside
      className={`bg-white border-r border-[#E2E0D8] flex flex-col h-screen shrink-0 relative transition-all duration-300 ${
        collapsed ? 'w-[60px]' : 'w-60'
      }`}
      data-testid="sidebar"
    >
      {/* Collapse toggle arrow */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-7 z-10 w-6 h-6 bg-white border border-[#E2E0D8] rounded-full flex items-center justify-center text-[#5C5C55] hover:text-[#1C1C19] hover:bg-[#F7F6F2] shadow-sm transition-colors"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Logo */}
      <div className="px-3 py-5 border-b border-[#E2E0D8]">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5 px-2'}`}>
          <div className="w-8 h-8 rounded-md bg-[#284A33] flex items-center justify-center shrink-0">
            <FileText className="text-white" size={16} />
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-heading text-base font-bold tracking-tight text-[#1C1C19] leading-tight">StatementSort</h1>
              <p className="text-[10px] text-[#5C5C55] tracking-wide uppercase">Expense Tracker</p>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {navItems.map((item, idx) => {
          if (item.type === 'divider') {
            return collapsed ? (
              <div key={`div-${idx}`} className="border-t border-[#E2E0D8] my-2" />
            ) : (
              <div key={`div-${idx}`} className="pt-3 pb-1 px-3">
                <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#B5B3A9]">{item.label}</span>
              </div>
            );
          }
          const { to, icon: Icon, label, testId } = item;
          return (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              data-testid={testId}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? 'bg-[#284A33] text-white font-medium'
                    : 'text-[#5C5C55] hover:bg-[#E8E6DE]/50 hover:text-[#1C1C19]'
                }`
              }
            >
              <Icon size={17} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User / Logout */}
      <div className="p-2 border-t border-[#E2E0D8]">
        {!collapsed && (
          <div className="flex items-center gap-2.5 px-3 py-2 mb-1">
            <div className="w-7 h-7 rounded-full bg-[#284A33] flex items-center justify-center text-white text-xs font-medium shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#1C1C19] truncate leading-tight">{user?.name || 'User'}</p>
              <p className="text-[11px] text-[#5C5C55] truncate">{user?.email}</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center py-2 mb-1">
            <div className="w-7 h-7 rounded-full bg-[#284A33] flex items-center justify-center text-white text-xs font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          title={collapsed ? 'Sign Out' : undefined}
          className={`flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2 w-full rounded-md text-sm text-[#5C5C55] hover:bg-[#E8E6DE]/50 hover:text-[#1C1C19] transition-all`}
        >
          <LogOut size={17} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </aside>
  );
}
